import React from 'react';
import { useParams } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import { TabList } from '@/components/ui/navigation/TabList';
import { EmployeeSectionNav } from '@/components/features/employee/EmployeeSectionNav';
import { useEmployeeProfile } from '@/hooks/employee/useEmployeeProfile';
import { EmployeeProfilePersonalDataForm } from '@/components/features/employee/employeeProfilePersonalDataForm';
import { EmployeeProfileContactDataForm } from '@/components/features/employee/employeeProfileContactDataForm';
import { EmployeeProfileAddressDataForm } from '@/components/features/employee/employeeProfileAddressDataForm';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useTranslation } from '@/i18n';
import { buildEmployeeFullName, formatEmployeeIdentity } from '@/utils';


export const EmployeeProfile: React.FC = () => {
  const { t: translate } = useTranslation();
  const { employeeId } = useParams<{ employeeId: string }>();
  const numericEmployeeId = employeeId ? Number(employeeId) : undefined;
  const {
    isLoading,
    error,
    profile,
    activeTab,
    setActiveTab,
    handleCancel,
    syncAssistance,
    genderOptions,
    maritalStatusOptions,
    employmentStatusOptions,
    regionOptions,
    communeOptions,
    workRegionOptions,
    workCommuneOptions,
    personalForm,
    contactForm,
    addressForm,
  } = useEmployeeProfile();

  if (error) {
    return (
      <PageLayout>
        <div className="flex flex-col gap-4 p-4" data-testid="employee-profile-error">
          <div className="text-red-600 text-sm">{error}</div>
          <div>
            <PrimaryButton variant="outline" onClick={handleCancel}>Volver al listado</PrimaryButton>
          </div>
        </div>
      </PageLayout>
    );
  }

  const tabs = [
    {
      id: 'personal',
      label: 'Datos personales',
      content: (
        <EmployeeProfilePersonalDataForm
          profile={profile}
          genderOptions={genderOptions}
          maritalStatusOptions={maritalStatusOptions}
          employmentStatusOptions={employmentStatusOptions}
          syncAssistance={syncAssistance}
          onCancel={handleCancel}
          personalForm={personalForm}
        />
      ),
    },
    {
      id: 'contact',
      label: 'Datos del contacto',
      content: (
        <EmployeeProfileContactDataForm
          profile={profile}
          onCancel={handleCancel}
          contactForm={contactForm}
        />
      ),
    },
    {
      id: 'address',
      label: 'Dirección',
      content: (
        <EmployeeProfileAddressDataForm
          profile={profile}
          regionOptions={regionOptions}
          communeOptions={communeOptions}
          workRegionOptions={workRegionOptions}
          workCommuneOptions={workCommuneOptions}
          onCancel={handleCancel}
          addressForm={addressForm}
        />
      ),
    },
  ];

  const identity = formatEmployeeIdentity(buildEmployeeFullName(profile ?? {}), profile?.rutFull);
  const dynamicTitle = identity
    ? translate('employee:breadcrumb.profile', { identity })
    : undefined;

  const sectionNav = numericEmployeeId ? (
    <EmployeeSectionNav employeeId={numericEmployeeId} activeSection="profile" />
  ) : undefined;

  return (
    <PageLayout dynamicTitle={dynamicTitle} isLoading={isLoading} headerContent={sectionNav}>
      <div data-testid="employee-profile-page">
        <TabList
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={(id) => setActiveTab(id as any)}
        />
      </div>
    </PageLayout>
  );
};

export default EmployeeProfile;
